import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import { z } from 'zod'

// 400系/500系エラーのときにharami_api_serverが返してくるレスポンスの型
const ApiErrorScheme = z.object({
  status: z.number(),
  code: z.nativeEnum(ErrorCodeEnum),
  errors: z.array(z.string()),
})
export type ApiError = z.infer<typeof ApiErrorScheme>

export type RequestError =
  | ({
      type: 'apiError'
    } & ApiError)
  | {
      type: 'unexpectedError'
    }

/**
 * クライアントからのリクエストに失敗した際にthrowされるエラーをRequestError型へ変換する
 *
 * @param error - harami_api_clientへのリクエストに失敗した際にthrowされるエラー
 */
export const getRequestError = async (
  error: unknown,
): Promise<RequestError> => {
  if (!(error instanceof Response)) {
    return { type: 'unexpectedError' }
  }
  const json = await error
    .clone()
    .json()
    .catch(e => e)
  const result = ApiErrorScheme.safeParse(json)
  if (!result.success) {
    return { type: 'unexpectedError' }
  }
  return {
    type: 'apiError',
    ...result.data,
  }
}

export const isApiError = (
  error: RequestError,
): error is Extract<RequestError, { type: 'apiError' }> => {
  return error.type === 'apiError'
}

export const isAppVersionInconsistentError = (error: RequestError) => {
  return (
    error.type === 'apiError' &&
    error.status === 400 &&
    error.code === ErrorCodeEnum.HaramiReportVersionInconsistentError
  )
}

export const isAuthError = (error: RequestError) => {
  return error.type === 'apiError' && isAuthErrorCode(error.code)
}

export const isAuthErrorCode = (code: ErrorCodeEnum) => {
  const authErrorCodes = [
    ErrorCodeEnum.AuthenticationExpired,
    ErrorCodeEnum.CredentialsNotFoundError,
    ErrorCodeEnum.ServiceAssignmentRequiredError,
    ErrorCodeEnum.InvalidAudienceError,
    ErrorCodeEnum.AccessTokenInactiveError,
  ]
  return authErrorCodes.includes(code)
}

export const isConflictError = (error: RequestError) => {
  return (
    error.type === 'apiError' &&
    error.status === 409 &&
    error.code === ErrorCodeEnum.ConflictError
  )
}

// harami_api_server が承認済みレポートへの不正な更新（ステータス巻き戻り）を検知した場合に返すエラー。
export const isReportAlreadyCompletedError = (error: RequestError) => {
  return (
    error.type === 'apiError' &&
    error.status === 409 &&
    error.code === ErrorCodeEnum.ReportAlreadyCompletedError
  )
}

export const isValidateError = (error: RequestError) => {
  return (
    error.type === 'apiError' &&
    error.status === 400 &&
    error.code === ErrorCodeEnum.BadRequestError
  )
}

/**
 * fetch() がネットワークレベルで失敗した場合、Fetch Standard の仕様により TypeError がスローされる。
 * HTTP エラー（認証エラー含む）は Response オブジェクトとして throw されるため、TypeError とは区別できる。
 * エラーメッセージ文字列（'Failed to fetch' 等）はブラウザ実装依存のため、チェックしない。
 */
export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError

/**
 * リクエストが中断された際にスローされるエラーを判定する。
 * fetch の中断（画面遷移・リロード・タイムアウト）では name='AbortError' の DOMException がスローされる。
 * DOMException は instanceof Error にならない環境があるため、name を duck typing で見る。
 */
export const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  (error as { name?: unknown }).name === 'AbortError'
